import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Select as BaseSelect } from "@base-ui/react/select";
import { useControlContext } from "@frontend/contexts";

import { SelectContext, makeSelectContext } from "./Select.context";
import type { SelectProps } from "./Select.types";
import {
  SELECT_EFFECT_RUNNERS,
  type SelectAcknowledgement,
} from "./selectEffects";

const ITEM_SELECTOR = '[data-slot="select-item"]';

function acknowledgedItem(event: Event): HTMLElement | null {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return null;
  }

  const { target } = event;

  return target instanceof Element
    ? target.closest<HTMLElement>(ITEM_SELECTOR)
    : null;
}

export function Select<Value, Multiple extends boolean | undefined = false>({
  size,
  layout = "default",
  children,
  effect = "press",
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  clearable = false,
  value: valueProp,
  defaultValue,
  onValueChange,
  ...props
}: SelectProps<Value, Multiple>): JSX.Element {
  type CommittedValue = Parameters<
    NonNullable<BaseSelect.Root.Props<Value, Multiple>["onValueChange"]>
  >[0];

  const inherited = useControlContext();
  const resolvedSize = size ?? inherited.size;

  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const [internalValue, setInternalValue] = useState<CommittedValue>(
    (defaultValue ?? null) as CommittedValue,
  );

  const [leftInset, setLeftInset] = useState(0);

  const acknowledgementRef = useRef<SelectAcknowledgement | null>(null);

  useEffect(() => () => acknowledgementRef.current?.cancel(), []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean, details: BaseSelect.Root.OpenChangeEventDetails) => {
      const runner =
        !nextOpen && details.reason === "item-press"
          ? SELECT_EFFECT_RUNNERS[effect]
          : undefined;

      const item = runner ? acknowledgedItem(details.event) : null;

      if (runner && item) {
        details.cancel();

        acknowledgementRef.current?.cancel();
        acknowledgementRef.current = runner(item, () => {
          acknowledgementRef.current = null;
          setInternalOpen(false);
          onOpenChange?.(false, details);
        });

        return;
      }

      setInternalOpen(nextOpen);
      onOpenChange?.(nextOpen, details);
    },
    [effect, onOpenChange],
  );

  const value = valueProp === undefined ? internalValue : valueProp;

  const handleValueChange = useCallback(
    (next: CommittedValue, details: BaseSelect.Root.ChangeEventDetails) => {
      setInternalValue(next);
      onValueChange?.(next, details);
    },
    [onValueChange],
  );

  const clear = useCallback(() => {
    const empty = (props.multiple ? [] : null) as CommittedValue;

    setInternalValue(empty);
    onValueChange?.(empty);
  }, [props.multiple, onValueChange]);

  const hasValue = Array.isArray(value) ? value.length > 0 : value != null;

  const context = useMemo(
    () =>
      makeSelectContext({
        size: resolvedSize,
        layout,
        effect,
        leftInset,
        onLeftInsetChange: setLeftInset,
        clearable,
        hasValue,
        clear,
      }),
    [resolvedSize, layout, effect, leftInset, clearable, hasValue, clear],
  );

  return (
    <SelectContext.Provider value={context}>
      <BaseSelect.Root
        data-slot="select-root"
        open={openProp ?? internalOpen}
        onOpenChange={handleOpenChange}
        value={clearable ? value : valueProp}
        defaultValue={clearable ? undefined : defaultValue}
        onValueChange={handleValueChange}
        {...props}
      >
        {children}
      </BaseSelect.Root>
    </SelectContext.Provider>
  );
}
